import { useGeoinformationStore } from '~/stores/geoinformationStore/geoinformationStore';
import { buildFaroGeoAttributes } from '~/utils/faro/buildFaroGeoAttributes';
import { sanitizeFaroPayload } from '~/utils/faro/sanitizeFaroPayload';

function hasStatisticsConsent(): boolean {
  // Cookiebot is injected via GTM, which does not run on localhost. Without this
  // dev fallback Faro can never start locally.
  if (import.meta.dev && !window.Cookiebot) {
    return true;
  }
  return window.Cookiebot?.consent?.statistics === true;
}

function parseSampleRate(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return 0.2;
  }
  return Math.min(1, Math.max(0, parsed));
}

export default defineNuxtPlugin({
  name: 'faro',
  setup(nuxtApp) {
    const {
      public: { faro },
      app
    } = useRuntimeConfig();

    if (!faro?.enabled || !faro.url) {
      return;
    }

    const logger = useLogger();
    const geoinformationStore = useGeoinformationStore();
    let started = false;

    const syncFaroConsent = () => {
      if (!started) {
        startFaro();
        return;
      }
      if (hasStatisticsConsent()) {
        window.faro?.unpause();
        return;
      }
      window.faro?.pause();
    };

    const startFaro = () => {
      if (started || !hasStatisticsConsent()) {
        return;
      }
      started = true;

      void (async () => {
        try {
          const geolocationPromise = geoinformationStore
            .init()
            .catch((error: unknown) => {
              logger.error('FARO GEOLOCATION ERROR', error);
            });
          const [
            {
              createSession,
              initializeFaro,
              getWebInstrumentations,
              InternalLoggerLevel
            },
            { TracingInstrumentation }
          ] = await Promise.all([
            import('@grafana/faro-web-sdk'),
            import('@grafana/faro-web-tracing')
          ]);

          const initialGeoAttributes = buildFaroGeoAttributes(
            geoinformationStore.detectedCountry,
            geoinformationStore.detectedRegion
          );
          const faroInstance = initializeFaro({
            url: faro.url,
            paused: !hasStatisticsConsent(),
            internalLoggerLevel: import.meta.dev
              ? InternalLoggerLevel.VERBOSE
              : InternalLoggerLevel.ERROR,
            app: {
              name: 'basler-webapp',
              version: String(app.buildId ?? ''),
              environment: faro.environment || 'local'
            },
            sessionTracking: {
              samplingRate: parseSampleRate(faro.sampleRate),
              session: createSession(initialGeoAttributes)
            },
            // ponytail: no public source maps; stacks stay minified until a private map upload exists
            instrumentations: [
              ...getWebInstrumentations({
                captureConsole: false,
                enablePerformanceInstrumentation: false
              }),
              new TracingInstrumentation()
            ],
            beforeSend: (item) => sanitizeFaroPayload(item)
          });

          await geolocationPromise;

          const geoAttributes = buildFaroGeoAttributes(
            geoinformationStore.detectedCountry,
            geoinformationStore.detectedRegion
          );
          const session = faroInstance.metas.value.session;
          const hasChanged =
            session &&
            Object.entries(geoAttributes).some(
              ([key, value]) => session.attributes?.[key] !== value
            );

          if (session && hasChanged) {
            faroInstance.api.setSession({
              ...session,
              attributes: {
                ...session.attributes,
                ...geoAttributes
              }
            });
          }

          if (!hasStatisticsConsent()) {
            window.faro?.pause();
          }
        } catch (error: unknown) {
          started = false;
          logger.error('FARO INIT ERROR', error);
        }
      })();
    };

    window.addEventListener('CookiebotOnConsentReady', syncFaroConsent);
    window.addEventListener('CookiebotOnAccept', syncFaroConsent);
    window.addEventListener('CookiebotOnDecline', syncFaroConsent);
    // Cookiebot loads via GTM and may have fired its consent events before this
    // plugin registered, so re-check once the app is mounted (mirrors plugin 29).
    nuxtApp.hook('app:mounted', syncFaroConsent);
    syncFaroConsent();
  }
});
